const DEFAULT_WIDTH = 15;
const DEFAULT_CORNER_SIZE = 11;
const DEFAULT_MIN_MIDDLE_WIDTH = 0;
const DEFAULT_HEIGHT_RATIO = 1;
const DEFAULT_OFFSET_Y = 0;
const DEFAULT_TINT = 0xFFFFFF;
const DEFAULT_SSHEET = 'ssheet';
const DEFAULT_FRAME = 'move_extra_label';
const DEFAULT_OVERLAP = false;

/**
 * class for configuring OMT_UI_ScrollBar
 */
export default class OMT_UI_ScrollBarConfig {
  /**
   * constructor
   * @param {number} width total width of scrollbar. result will be scaled to fit.
   * @param {number} cornerSize 9-scale corner size
   * @param {number} minMiddleWidth minimum width for middle for 9-scale. width will still be enforced by scale.
   * @param {number} heightRatio percentage should be 0-1
   * @param {number} offsetY value to offset the scrollbars y position, default 0
   * @param {number} tint tint to apply to the knob
   * @param {string} knobAssetSheet sprite sheet for knob asset
   * @param {string} knobAssetFrame frame for knob asset
   * @param {boolean} overlapScrollArea set to false if you want to overlap the scrollbar in the content area
   * @param {string} scrollBackground some background used
   * @param {boolean} autoHide set if the scrollbar should auto hide content height is less then the view height
   */
  constructor(width, cornerSize, minMiddleWidth, heightRatio, offsetY, tint, knobAssetSheet, knobAssetFrame, overlapScrollArea, scrollBackground, autoHide) {
    this._width = width || DEFAULT_WIDTH;
    this._cornerSize = cornerSize || DEFAULT_CORNER_SIZE;
    this._minMiddleWidth = minMiddleWidth || DEFAULT_MIN_MIDDLE_WIDTH;
    this._heightRatio = heightRatio || DEFAULT_HEIGHT_RATIO;
    this._offsetY = offsetY || DEFAULT_OFFSET_Y;
    this._tint = tint || DEFAULT_TINT;
    this._knobAssetSheet = knobAssetSheet || DEFAULT_SSHEET;
    this._knobAssetFrame = knobAssetFrame || DEFAULT_FRAME;
    this._overlapScrollArea = overlapScrollArea || DEFAULT_OVERLAP;
    this._scrollBackground = scrollBackground || null;
    this._autoHide = autoHide || true;
  }

  /**
   * set scroll bar width
   * @param {number} value
   */
  set width(value) {
    this._width = value;
  }

  /**
   * get scroll bar width
   * @returns {number}
   */
  get width() {
    return this._width;
  }

  /**
   * set 9-scale corner size
   * @param {number} value
   */
  set cornerSize(value) {
    this._cornerSize = value;
  }

  /**
   * get 9-scale corner size
   * @returns {number}
   */
  get cornerSize() {
    return this._cornerSize;
  }

  /**
   * set minimum middle width
   * @param {number} value
   */
  set minMiddleWidth(value) {
    this._minMiddleWidth = value;
  }

  /**
   * get minimum middle width
   * @returns {number}
   */
  get minMiddleWidth() {
    return this._minMiddleWidth;
  }

  /**
   * set the % height of the scrollbar in relation to the scrollArea height
   * @param {number} value
   */
  set heightRatio(value) {
    this._heightRatio = value;
  }

  /**
   * get the % height of the scrollbar in relation to the scrollArea height
   * @returns {number} should be 0-1
   */
  get heightRatio() {
    return this._heightRatio;
  }

  /**
   * set the y offset value for the scrollbar
   * @param {number} value
   */
  set offsetY(value) {
    this._offsetY = value;
  }

  /**
   * get the y offset value for the scrollbar
   * @returns {number} can be negative or positive
   */
  get offsetY() {
    return this._offsetY;
  }

  /**
   * set the knob asset sheet
   * @param {number} value
   */
  set knobAssetSheet(value) {
    this._knobAssetSheet = value;
  }

  /**
   * get the knob asset sheet
   * @returns {string}
   */
  get knobAssetSheet() {
    return this._knobAssetSheet;
  }

  /**
   * set the knob asset frame
   * @param {number} value
   */
  set knobAssetFrame(value) {
    this._knobAssetFrame = value;
  }

  /**
   * get the knob asset frame
   * @returns {string}
   */
  get knobAssetFrame() {
    return this._knobAssetFrame;
  }

  /**
   * set the scroll background frame, Assumes to be in the same sheet as knobAssetFrame and the same slices
   * @param {string} value
   */
  set scrollBackground(value) {
    this._scrollBackground = value;
  }

  /**
   * Get the scroll background frame, Assumes to be in the same sheet as knobAssetFrame and the same slices
   * @returns {string}
   */
  get scrollBackground() {
    return this._scrollBackground;
  }

  /**
   * set the knob tint
   * @param {number} value
   */
  set knobTint(value) {
    this._tint = value;
  }

  /**
   * get the knob tint
   * @returns {number}
   */
  get knobTint() {
    return this._tint;
  }

  /**
   * set scroll area masking state
   * @param {boolean} value
   */
  set overlapScrollArea(value) {
    this._overlapScrollArea = value;
  }

  /**
   * true if scrollbar area needs to be masked
   * @returns {boolean}
   */
  get overlapScrollArea() {
    return this._overlapScrollArea;
  }

  /**
   * set if the scroll bar should auto-hide if content is smaller then view
   * @param {boolean} value
   */
  set autoHide(value) {
    this._autoHide = value;
  }

  /**
   * get if the scroll bar should auto-hide if content is smaller then view
   * @returns {boolean}
   */
  get autoHide() {
    return this._autoHide;
  }
}
